'use client';

import { useCallback } from 'react';

import { getMinLength, hasMinimumLength, validateId } from '@/core/utils/order.util';

/**
 * Hook para validar un pedido y obtener información relacionada.
 *
 * @param idType - Tipo de ID del pedido.
 *
 * @param idValue - Valor del ID del pedido.
 *
 * @returns Un objeto con funciones de validación y datos relacionados.
 */
export function useOrderValidation(idType: string, idValue: string) {
  /**
   * Valida el ID del pedido según su tipo.
   *
   * @returns `true` si el ID es válido, de lo contrario `false`.
   */
  const validateOrder = useCallback(() => {
    return validateId(idType, idValue);
  }, [idType, idValue]);

  /**
   * Obtiene el placeholder para el campo de ID del documento.
   *
   * @returns El placeholder correspondiente al tipo de ID.
   */
  const getDocumentPlaceholder = useCallback((): string => {
    switch (idType) {
      case 'pickslip':
        return '2253674';
      case 'pedido':
        return 'placeholders:pedido';
      case 'factura':
        return 'placeholders:orders';
      default:
        return 'placeholders:orders';
    }
  }, [idType]);

  const hasMinLength = hasMinimumLength(idType, idValue);
  const minLength = getMinLength(idType);

  return {
    validateOrder,
    getDocumentPlaceholder,
    hasMinLength,
    minLength
  };
}
